/**
 * @deprecated This module is being replaced by MediaCoordinator.
 * See coordinators/media/MediaCoordinator.ts.
 * Once all consumers are migrated, this file will be deleted.
 */
// import constants from '@playcastdotio/GlobalConfig';
import { AudioState, PlaycastChannels, PlaycastPeerMediaMetadata, VideoState } from '@playcastdotio/CoreTypes';
import { getStateStore } from '../../stores';
import { getElectronStores } from '../../stores/electronStores';
import { enableTracksByChannel, notifyTracksChanged } from '../peers/host';
import { notifyTracksChanged as rcNotifyTracks, enableTracksByChannel as enableRcTracksByChannel } from '../peers/remoteControl';
import { PlaycastSendProfile } from '@playcastdotio/Peer';
import { createVolumeController } from '@playcastdotio/VolumeController';
import { hostState, matchmakerOverrideState } from '../../stores/shared';
import { USE_MEDIA_COORDINATOR } from '../../../config/featureFlags';

const state = getStateStore('ipc');
const electronState = getElectronStores();

const videoStreams: {
  [key: string]: { track: MediaStreamTrack; metadata: PlaycastPeerMediaMetadata } | undefined;
} = {};
const audioStreams: {
  [key: string]:
    | {
        track: MediaStreamTrack;
        metadata: PlaycastPeerMediaMetadata;
        volumeControl: (volume: number) => void;
        audioContext: AudioContext;
        analyser?: AnalyserNode;
        dataArray?: Uint8Array;
      }
    | undefined;
} = {};

const createAudioAnalyser = (stream: MediaStreamTrack, audioContext: AudioContext) => {
  const analyser = audioContext.createAnalyser();
  analyser.fftSize = 128; // Reduced from 256 to lower CPU usage
  const bufferLength = analyser.frequencyBinCount;
  const dataArray = new Uint8Array(bufferLength);

  const source = audioContext.createMediaStreamSource(new MediaStream([stream]));
  source.connect(analyser);
  // Remove the connection to destination as the volume controller already handles this
  // analyser.connect(audioContext.destination);

  return { analyser, dataArray };
};

const getAudioLevel = (analyser: AnalyserNode, dataArray: Uint8Array<ArrayBuffer>) => {
  analyser.getByteFrequencyData(dataArray);
  // Use a weighted average that emphasizes higher amplitudes
  const values = Array.from(dataArray);
  const total = values.reduce((acc, val) => acc + val * val, 0);
  const rms = Math.sqrt(total / values.length);
  return rms;
};

let populateDisplayTimer: ReturnType<typeof setTimeout> | null = null;
let lastDeviceListKey = '';

const deviceListKey = (shadowcams: { deviceId: string }[], cameras: { deviceId: string }[]) =>
  [...shadowcams.map((s) => s.deviceId), ...cameras.map((c) => c.deviceId)].sort().join('|');

const populateDisplaySelectionNow = () => {
  navigator.mediaDevices.enumerateDevices().then((devices) => {
    const cameras = devices.filter((device) => device.kind === 'videoinput');
    console.debug('[MediaManager] Camera Devices:', cameras);
    // const cameraFilters = cameras.map((camera) => camera.label);
    const currentState = electronState.driverState.currentState;
    // const filteredCameras = cameras.filter((camera) => cameraFilters.some((filter) => camera.label.includes(filter)));
    // if the camera is in driverStore and is false, remove it
    const filteredAvailableCameras = cameras.filter((camera) => {
      const driverState = currentState[camera.label];
      if (driverState !== undefined) {
        return driverState;
      }
      return true;
    });
    // make a list of ShadowCams
    const shadowcams = filteredAvailableCameras.filter((camera) => camera.label.includes('ShadowCam'));
    // remove shadowcams from available cameras
    const filteredAvailableCamerasWithoutShadowcams = filteredAvailableCameras.filter((camera) => !camera.label.includes('ShadowCam'));
    const regex = / \([0-9a-fA-F]+:[0-9a-fA-F]+\)$/;
    const camerasWithFilteredIds = filteredAvailableCamerasWithoutShadowcams.map((camera) => {
      return {
        deviceId: camera.deviceId,
        displayName: camera.label.replace(regex, '').trim(),
      };
    });

    const availableCameras = camerasWithFilteredIds.map((camera) => {
      return {
        deviceId: camera.deviceId,
        displayName: camera.displayName,
      };
    });
    const availableShadowcams = shadowcams.map((camera) => {
      return {
        deviceId: camera.deviceId,
        displayName: camera.label,
      };
    });

    const nextKey = deviceListKey(availableShadowcams, availableCameras);
    if (nextKey === lastDeviceListKey) {
      return;
    }
    lastDeviceListKey = nextKey;

    state.videoState.updateState({
      ...state.videoState.currentState,
      cameras: availableCameras,
      shadowcams: availableShadowcams,
    });
    if (!USE_MEDIA_COORDINATOR) {
      void refreshDisplayShadowcamStreams();
    }
  });
};

export const populateDisplaySelection = () => {
  if (populateDisplayTimer) {
    clearTimeout(populateDisplayTimer);
  }
  populateDisplayTimer = setTimeout(() => {
    populateDisplayTimer = null;
    populateDisplaySelectionNow();
  }, 300);
};

export const populateMicrophoneSelection = () => {
  // If not in standard edition, don't initialize microphones
  if (state.hostState.currentState.edition !== 'standard') {
    state.audioState.updateState({
      ...state.audioState.currentState,
      microphoneSources: [
        {
          deviceId: '0',
          label: 'None',
          selected: true,
        },
      ],
    });
    return;
  }

  navigator.mediaDevices.enumerateDevices().then((devices) => {
    const microphones = devices.filter((device) => device.kind === 'audioinput');
    const defaultMic = microphones.find((microphone) => microphone.deviceId === 'default')?.label;
    const filteredMics = microphones.filter(
      (microphone) => !microphone.label.startsWith('Default') && !microphone.label.startsWith('Communications'),
    );
    const regex = / \([0-9a-fA-F]+:[0-9a-fA-F]+\)$/;
    const parsedNames = filteredMics.map((microphone) => {
      return {
        deviceId: microphone.deviceId,
        label: microphone.label.replace(regex, '').trim(),
      };
    });
    const deduped = parsedNames.filter((microphone, index, self) => self.findIndex((t) => t.label === microphone.label) === index);
    const availableMicrophones = deduped.map((microphone, index) => {
      return {
        deviceId: microphone.deviceId,
        label: microphone.label,
        // selected: defaultMic ? defaultMic.includes(microphone.label) : index === 0,
        selected: localStorage.getItem('selectedMicId') === microphone.deviceId,
      };
    });
    availableMicrophones.sort((a, b) => {
      if (a.selected) return -1;
      if (b.selected) return 1;
      return 0;
    });
    // add "None" option
    availableMicrophones.unshift({
      deviceId: '0',
      label: 'None',
      selected: availableMicrophones.every((mic) => !mic.selected),
    });
    console.debug('[Mics] Available microphones', availableMicrophones);
    console.debug('[Mics] Default mic', defaultMic);
    state.audioState.updateState({
      ...state.audioState.currentState,
      microphoneSources: availableMicrophones,
    });
  });
};

export const populateDesktopAudioSelection = async () => {
  console.log('🔧 HOST: populateDesktopAudioSelection called');
  console.log('🔧 HOST: Current audioStreams keys before populate:', Object.keys(audioStreams));

  // DON'T create tracks here - just populate the dropdown options
  // Tracks will be created on-demand in initializeAudioStreams()

  // Just build the dropdown options - don't create tracks yet
  const desktopAudioSources = [
    {
      deviceId: '0',
      label: 'None',
      selected: localStorage.getItem('selectedDesktopAudioSource') === '0',
    },
    {
      deviceId: 'desktop',
      label: 'Desktop Audio',
      selected: localStorage.getItem('selectedDesktopAudioSource') === 'desktop' || true,
    },
  ];

  // Check if game capture is available and add to options
  if (matchmakerOverrideState.currentState || hostState.currentState.edition === 'standard') {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const gameCaptureDevice = devices.find((device) => device.label.includes('AudioCaster Virtual Device'));
      if (gameCaptureDevice) {
        console.log('🔧 HOST: Game capture device found, adding to options');
        desktopAudioSources.push({
          deviceId: 'gameCapture',
          label: 'Game Capture',
          selected: localStorage.getItem('selectedDesktopAudioSource') === 'gameCapture',
        });
      } else {
        console.log('🔧 HOST: Game capture device not found');
      }
    } catch (e) {
      console.debug('[Media] Error checking for game capture device:', e);
    }
  }

  const currentState = state.audioState.currentState;
  const currentSelectedSource = currentState.desktopAudioSources.find(source => source.selected);

  // Update the sources while preserving the current selection
  const updatedSources = desktopAudioSources.map(source => ({
    ...source,
    selected: currentSelectedSource ? source.deviceId === currentSelectedSource.deviceId : source.deviceId === 'desktop'
  }));

  state.audioState.updateState({
    ...currentState,
    desktopAudioSources: updatedSources,
  });
};

const initializeVideoStreams = async () => {
  let anyVideoStreamReady = false;
  const videoDevices = Object.keys(videoStreams);
  for (const deviceId of videoDevices) {
    if (videoStreams[deviceId] === undefined) {
      if (deviceId === '0') {
        continue;
      }
      if (deviceId.startsWith('display') || deviceId.startsWith('followMouse')) {
        // Get all active shadowcams
        // if the selected deviceid is device-shadowcamxx, initialize the shadowcam
        const shadowcamName = deviceId.split('-')[1];
        const shadowcam = state.videoState.currentState.shadowcams.find((shadowcam) => shadowcam.displayName.includes(shadowcamName));
        if (shadowcam) {
          try {
            let channel: PlaycastPeerMediaMetadata['channel'] = 'default';
            if (shadowcam.displayName.includes('ShadowCam00')) {
              channel = 'hostShadowCam00Video';
            } else if (shadowcam.displayName.includes('ShadowCam01')) {
              channel = 'hostShadowCam01Video';
            } else if (shadowcam.displayName.includes('ShadowCam02')) {
              channel = 'hostShadowCam02Video';
            } else if (shadowcam.displayName.includes('ShadowCam03')) {
              channel = 'hostShadowCam03Video';
            } else if (shadowcam.displayName.includes('ShadowCam04')) {
              channel = 'hostShadowCam04Video';
            } else if (shadowcam.displayName.includes('ShadowCam05')) {
              channel = 'hostShadowCam05Video';
            } else if (shadowcam.displayName.includes('ShadowCam06')) {
              channel = 'hostShadowCam06Video';
            } else if (shadowcam.displayName.includes('ShadowCam07')) {
              channel = 'hostShadowCam07Video';
            } else if (shadowcam.displayName.includes('ShadowCam08')) {
              channel = 'hostShadowCam08Video';
            } else if (shadowcam.displayName.includes('ShadowCam09')) {
              channel = 'hostShadowCam09Video';
            }
            const stream = await navigator.mediaDevices.getUserMedia({
              video: {
                deviceId: { exact: shadowcam.deviceId },
                frameRate: { ideal: 60 },
                width: { ideal: 1280 },
                height: { ideal: 720 },
              }
            });

            // Optimize for gaming motion (not videoconferencing detail)
            const videoTrack = stream.getVideoTracks()[0];
            if (videoTrack) {
              videoTrack.contentHint = 'motion';
              console.log('✅ Video track optimized for gaming motion content');
            }

            const label = stream.getVideoTracks()[0].label;
            videoStreams[deviceId] = {
              track: stream.getVideoTracks()[0],
              metadata: { channel, displayName: `Host Video: ${label}`, type: 'video' },
            };
            anyVideoStreamReady = true;
          } catch (e) {
            console.error('[Media] Error initializing', shadowcamName, e);
          }
        } else {
          console.debug('[Media] Shadowcam not enumerated yet for preview slot:', shadowcamName, {
            knownShadowcams: state.videoState.currentState.shadowcams.map((s) => s.displayName),
          });
        }
      } else if (deviceId.startsWith('desktop')) {
        try {
          const stream = (await (navigator.mediaDevices as any).getUserMedia({
            video: {
              mandatory: {
                chromeMediaSource: 'desktop',
              },
            },
            audio: {
              mandatory: {
                chromeMediaSource: 'desktop',
              },
            },
          })) as MediaStream;
          const label = stream.getVideoTracks()[0].label;
          videoStreams[deviceId] = {
            track: stream.getVideoTracks()[0],
            metadata: { channel: 'default', displayName: `Host Desktop Video: ${label}`, type: 'video' },
          };
        } catch (e) {
          console.error('[Media] Error initializing desktop stream', e);
        }
      } else {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ video: { deviceId: { exact: deviceId } } });
          videoStreams[deviceId] = {
            track: stream.getVideoTracks()[0],
            metadata: { channel: 'hostCamera', displayName: `Host Video: ${stream.getVideoTracks()[0].label}`, type: 'video' },
          };
        } catch (e) {
          // remove the device from the list
          removeVideoDevice(deviceId);
          console.error('[Media] Error initializing video stream for deviceId', deviceId, e);
        }
      }
    }
  }

  if (anyVideoStreamReady) {
    // Nudge Preview.tsx (legacy getMediaTrackFromIdAsPromise) after async getUserMedia completes.
    state.videoState.updateState({
      ...state.videoState.currentState,
    });
  }
};

/**
 * Re-open getUserMedia on active display shadowcams after Chromium enumerates them.
 * PlayJector activates ShadowCam03 ~700ms after boot; without this, legacy Preview can
 * miss the window and stay black until a full videoState churn.
 */
let refreshShadowcamStreamsTimer: ReturnType<typeof setTimeout> | null = null;
let refreshShadowcamStreamsInFlight: Promise<void> | null = null;

const refreshDisplayShadowcamStreamsNow = async (): Promise<void> => {
  const videoState = state.videoState.currentState;
  if (videoState.selectedDevice === '0') {
    return;
  }

  const deviceIds: string[] = [];
  if (videoState.selectedDevice === 'display' || videoState.selectedDevice === 'followMouse') {
    const prefix = videoState.selectedDevice === 'followMouse' ? 'followMouse' : 'display';
    state.activeShadowcamState.currentState.active.forEach((shadowcam) => {
      deviceIds.push(`${prefix}-${shadowcam}`);
    });
  } else {
    deviceIds.push(videoState.selectedDevice);
  }

  deviceIds.forEach((deviceId) => {
    const existing = videoStreams[deviceId];
    if (existing === undefined || existing.track.readyState !== 'live') {
      videoStreams[deviceId] = undefined;
    }
  });

  await initializeVideoStreams();
};

export const refreshDisplayShadowcamStreams = (): Promise<void> => {
  if (refreshShadowcamStreamsInFlight) {
    return refreshShadowcamStreamsInFlight;
  }

  return new Promise((resolve) => {
    if (refreshShadowcamStreamsTimer) {
      clearTimeout(refreshShadowcamStreamsTimer);
    }
    refreshShadowcamStreamsTimer = setTimeout(() => {
      refreshShadowcamStreamsTimer = null;
      refreshShadowcamStreamsInFlight = refreshDisplayShadowcamStreamsNow()
        .finally(() => {
          refreshShadowcamStreamsInFlight = null;
        })
        .then(resolve);
    }, 300);
  });
};

const initializeAudioStreams = async () => {
  const audioDevices = Object.keys(audioStreams);
  console.debug('🔧 HOST: initializeAudioStreams called for devices:', audioDevices);
  console.debug('🔧 HOST: Current audioStreams state:', Object.fromEntries(
    Object.entries(audioStreams).map(([key, stream]) => [
      key,
      stream ? {
        trackId: stream.track.id,
        channel: stream.metadata.channel,
        displayName: stream.metadata.displayName,
        trackReadyState: stream.track.readyState
      } : 'undefined'
    ])
  ));

  for (const deviceId of audioDevices) {
    if (audioStreams[deviceId] === undefined) {
      console.debug('🔧 HOST: Initializing audio device:', deviceId);

      if (deviceId === '0') {
        continue;
      }
      // Skip microphone initialization if not in standard edition
      if (deviceId !== 'desktop' && deviceId !== 'gameCapture' && state.hostState.currentState.edition !== 'standard') {
        continue;
      }
      if (deviceId === 'gameCapture') {
        // Only initialize game capture if matchmaker override is enabled
        if (!matchmakerOverrideState.currentState && hostState.currentState.edition !== 'standard') {
          console.debug('[Media] Game capture disabled - matchmaker override is false or not in standard edition');
          continue;
        }

        try {
          const devices = await navigator.mediaDevices.enumerateDevices();
          const gameCaptureDevice = devices.find((device) => device.label.includes('AudioCaster Virtual Device'));

          console.debug('[Media Diagnostic] Game Capture Device Search:', {
            found: !!gameCaptureDevice,
            deviceInfo: gameCaptureDevice
              ? {
                  deviceId: gameCaptureDevice.deviceId,
                  label: gameCaptureDevice.label,
                  kind: gameCaptureDevice.kind,
                }
              : null,
          });

          if (!gameCaptureDevice) {
            console.error('[Media] Game capture device not found');
            continue;
          }
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: {
              deviceId: { exact: gameCaptureDevice.deviceId },
            },
          });
          const track = stream.getAudioTracks()[0];
          const modifyableTrack = createVolumeController(track);
          modifyableTrack.setVolume(state.audioState.currentState.desktopAudioVolume);
          const { analyser, dataArray } = createAudioAnalyser(
            modifyableTrack.resultingStream.getAudioTracks()[0],
            modifyableTrack.audioContext,
          );
          const finalTrack = modifyableTrack.resultingStream.getAudioTracks()[0];
          console.log('🔧 HOST: Created game capture track:', {
            deviceId,
            trackId: finalTrack.id,
            trackLabel: finalTrack.label,
            trackReadyState: finalTrack.readyState
          });

          audioStreams[deviceId] = {
            track: finalTrack,
            metadata: { channel: 'hostDesktopAudio', displayName: 'Host Game Capture', type: 'audio' },
            volumeControl: modifyableTrack.setVolume,
            audioContext: modifyableTrack.audioContext,
            analyser,
            dataArray,
          };
        } catch (e) {
          console.error('[Media] Error initializing game capture stream', e);
        }
      } else if (deviceId === 'desktop') {
        try {
          const stream = (await (navigator.mediaDevices as any).getUserMedia({
            video: {
              mandatory: {
                chromeMediaSource: 'desktop',
              },
            },
            audio: {
              mandatory: {
                chromeMediaSource: 'desktop',
              },
            },
          })) as MediaStream;
          const track = stream.getAudioTracks()[0];
          const modifyableTrack = createVolumeController(track);
          modifyableTrack.setVolume(state.audioState.currentState.desktopAudioVolume);
          const { analyser, dataArray } = createAudioAnalyser(
            modifyableTrack.resultingStream.getAudioTracks()[0],
            modifyableTrack.audioContext,
          );
          const finalTrack = modifyableTrack.resultingStream.getAudioTracks()[0];
          console.log('🔧 HOST: Created desktop audio track:', {
            deviceId,
            trackId: finalTrack.id,
            trackLabel: finalTrack.label,
            trackReadyState: finalTrack.readyState
          });

          audioStreams[deviceId] = {
            track: finalTrack,
            metadata: { channel: 'hostDesktopAudio', displayName: 'Host Desktop Audio', type: 'audio' },
            volumeControl: modifyableTrack.setVolume,
            audioContext: modifyableTrack.audioContext,
            analyser,
            dataArray,
          };
        } catch (e) {
          console.error('[Media] Error initializing desktop audio stream', e);
        }
      } else {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: { deviceId: { exact: deviceId }, autoGainControl: false } });
        const track = stream.getAudioTracks()[0];
        const modifyableTrack = createVolumeController(track);
        modifyableTrack.setVolume(state.audioState.currentState.microphoneVolume);
        const { analyser, dataArray } = createAudioAnalyser(
          modifyableTrack.resultingStream.getAudioTracks()[0],
          modifyableTrack.audioContext,
        );
        const finalTrack = modifyableTrack.resultingStream.getAudioTracks()[0];
        console.log('🔧 HOST: Created microphone track:', {
          deviceId,
          trackId: finalTrack.id,
          trackLabel: finalTrack.label,
          trackReadyState: finalTrack.readyState
        });

        audioStreams[deviceId] = {
          track: finalTrack,
          metadata: { channel: 'hostMicrophone', displayName: `Host Microphone: ${stream.getAudioTracks()[0].label}`, type: 'audio' },
          volumeControl: modifyableTrack.setVolume,
          audioContext: modifyableTrack.audioContext,
          analyser,
          dataArray,
        };
      }
    }
  }
};

setInterval(() => {
  if (videoStreams) {
    const readyStates = Object.keys(videoStreams).map((key) => {
      return { key, state: videoStreams[key]?.track.readyState };
    });

    const endedStreams: string[] = [];
    readyStates.forEach((state) => {
      if (state.state === 'ended') {
        videoStreams[state.key] = undefined;
        endedStreams.push(state.key);
        console.log('[Media] Video stream ended for device. Refreshing.', state.key);
      }
    });

    // Only notify if we actually found ended streams
    if (endedStreams.length > 0) {
      console.warn('[Media] Video streams ended, reinitializing and notifying tracks:', endedStreams);
      setTimeout(() => {
        initializeVideoStreams().then(() => {
          rcNotifyTracks();
          notifyTracksChanged();
          state.videoState.updateState({
            ...state.videoState.currentState,
            selectedDevice: state.videoState.currentState.selectedDevice,
          });
        });
      }, 5000);
    }
  }
  // if (audioStreams) {
  //   // Log diagnostic information about audio streams
  //   const audioStreamInfo = Object.entries(audioStreams)
  //     .map(([deviceId, stream]) => {
  //       if (!stream) return null;

  //       // Get audio level if analyser is available
  //       let audioLevel = 0;
  //       if (stream.analyser && stream.dataArray) {
  //         audioLevel = getAudioLevel(stream.analyser, stream.dataArray);
  //       }

  //       return {
  //         deviceId,
  //         channel: stream.metadata.channel,
  //         displayName: stream.metadata.displayName,
  //         enabled: stream.track.enabled,
  //         readyState: stream.track.readyState,
  //         muted: stream.track.muted,
  //         audioLevel: Math.round(audioLevel), // 0-255 range
  //         audioLevelPercent: Math.round((audioLevel / 255) * 100), // 0-100 range
  //       };
  //     })
  //     .filter(Boolean);

  //   console.debug('[Media Diagnostic] Active Audio Streams:', audioStreamInfo);
  // }
}, 1000);

const removeAudioDevice = (deviceId: string) => {
  console.log('🗑️ HOST: Removing audio device:', deviceId);
  if (audioStreams[deviceId]) {
    const stream = audioStreams[deviceId];
    console.log(`🗑️ HOST: Stopping track for ${deviceId}:`, {
      trackId: stream?.track.id,
      channel: stream?.metadata.channel,
      displayName: stream?.metadata.displayName,
      trackReadyState: stream?.track.readyState
    });

    try {
      // Stop the track first
      stream?.track.stop();

      // Gracefully close audio context to prevent audio "crash"
      if (stream?.audioContext && stream.audioContext.state !== 'closed') {
        console.log('🗑️ HOST: Closing audio context for:', deviceId);
        stream.audioContext.close().catch(e => {
          console.warn(`🗑️ HOST: Error closing audio context for ${deviceId}:`, e);
        });
      }
    } catch (error) {
      console.error(`🗑️ HOST: Error during audio cleanup for ${deviceId}:`, error);
    }

    // Remove from streams object
    delete audioStreams[deviceId];
    console.log('✅ HOST: Successfully removed audio device:', deviceId);
  } else {
    console.log('⚠️ HOST: Tried to remove non-existent audio device:', deviceId);
  }
};

const removeVideoDevice = (deviceId: string) => {
  if (videoStreams[deviceId]) {
    videoStreams[deviceId]?.track?.stop();
    delete videoStreams[deviceId];
    console.warn('[Media] Stopped video stream for device', deviceId);
  }
};

let avPromises: Promise<void>[] = [];
let timeout: NodeJS.Timeout | null = null;
let keyCache: string[] = [];
const awaitAvPromises = async () => {
  if (timeout) {
    clearTimeout(timeout);
  }
  timeout = setTimeout(async () => {
    await Promise.all(avPromises).catch((e) => {
      console.error('[Media] Error initializing audio/video streams', e);
    });
    avPromises = [];
    // const filteredVideo: { [key: string]: { track: MediaStreamTrack; metadata: PlaycastPeerMediaMetadata } } = Object.keys(
    //   videoStreams,
    // ).reduce((acc, key) => {
    //   if (videoStreams[key] !== undefined) {
    //     acc[key] = videoStreams[key];
    //   }
    //   return acc;
    // }, {});
    // const filteredAudio: { [key: string]: { track: MediaStreamTrack; metadata: PlaycastPeerMediaMetadata } } = Object.keys(
    //   audioStreams,
    // ).reduce((acc, key) => {
    //   if (audioStreams[key] !== undefined) {
    //     acc[key] = audioStreams[key];
    //   }
    //   return acc;
    // }, {});
    // const combined = { ...filteredVideo, ...filteredAudio };
    // setTracks(combined);
    checkMutedState();
    const videoKeys = Object.keys(videoStreams).filter((key) => videoStreams[key] !== undefined);
    const audioKeys = Object.keys(audioStreams).filter((key) => audioStreams[key] !== undefined);
    const newKeyCache = [...videoKeys, ...audioKeys].sort(); // Sort to ensure consistent ordering

    // dont send desktop audio if selected is deviceId 0
    const selectedDesktopSource = state.audioState.currentState.desktopAudioSources.find((source) => source.selected);
    const selectedMicrophoneSource = state.audioState.currentState.microphoneSources.find((source) => source.selected);
    if (selectedDesktopSource?.deviceId === '0') {
      // remove from newKeyCache
      const index = newKeyCache.indexOf('desktop');
      if (index > -1) {
        newKeyCache.splice(index, 1);
      }
    }
    if (selectedMicrophoneSource?.deviceId === '0') {
      const index = newKeyCache.indexOf('0');
      if (index > -1) {
        newKeyCache.splice(index, 1);
      }
    }

    // Sort again after removals to ensure consistent ordering
    newKeyCache.sort();

    // Ensure keyCache is also sorted for proper comparison
    const sortedKeyCache = [...keyCache].sort();

    console.debug('[Media] Key cache comparison:', {
      oldKeys: sortedKeyCache,
      newKeys: newKeyCache,
      lengthChanged: newKeyCache.length !== keyCache.length,
      contentChanged: newKeyCache.some((key, index) => key !== sortedKeyCache[index]),
    });

    const keysChanged = newKeyCache.length !== keyCache.length || newKeyCache.some((key, index) => key !== sortedKeyCache[index]);

    if (keysChanged) {
      console.warn('[Media] Key cache changed. Notifying tracks', {
        from: sortedKeyCache,
        to: newKeyCache,
      });
      keyCache = newKeyCache;
      rcNotifyTracks();
      notifyTracksChanged();
    } else {
      console.debug('[Media] Key cache unchanged, skipping track notification');
    }
    timeout = null;
  }, 10);
};

// let micMutedState: boolean;
const checkMutedState = () => {
  const micMuted = state.audioState.currentState.microphoneMuted;
  const desktopMuted = state.audioState.currentState.desktopAudioMuted;

  console.debug('🔇 HOST: Checking mute state:', {
    microphoneMuted: micMuted,
    desktopAudioMuted: desktopMuted,
  });

  const audioDevices = Object.keys(audioStreams);
  console.debug('🔇 HOST: Active audio streams:', audioDevices);

  audioDevices.forEach((deviceId) => {
    const stream = audioStreams[deviceId];
    if (!stream) return;

    const previousState = stream.track.enabled;
    let newEnabledState = stream.track.enabled; // default to current state

    // Apply mute logic based on channel type
    if (stream.metadata.channel === 'hostMicrophone' || stream.metadata.channel === 'remoteHostMicrophone') {
      newEnabledState = !micMuted;
    }
    if (stream.metadata.channel === 'hostDesktopAudio' || stream.metadata.channel === 'remoteHostAudio') {
      newEnabledState = !desktopMuted;
    }

    // Only update if state actually changed
    if (previousState !== newEnabledState) {
      console.log('🔇 HOST: Changing track enabled state:', {
        deviceId,
        trackId: stream.track.id,
        channel: stream.metadata.channel,
        displayName: stream.metadata.displayName,
        previouslyEnabled: previousState,
        nowEnabled: newEnabledState,
      });
      stream.track.enabled = newEnabledState;
    }
  });
};

// Debounce volume changes to prevent audio context overload during rapid input
let volumeChangeTimeout: NodeJS.Timeout | null = null;
const setVolume = () => {
  // Clear existing timeout to debounce rapid volume changes
  if (volumeChangeTimeout) {
    clearTimeout(volumeChangeTimeout);
  }

  volumeChangeTimeout = setTimeout(() => {
    const audioState = state.audioState.currentState;
    const selectedDesktop = audioState.desktopAudioSources.find((source) => source.selected);

    console.debug('🔊 HOST: Setting volumes (debounced):', {
      desktopAudioVolume: audioState.desktopAudioVolume,
      microphoneVolume: audioState.microphoneVolume,
      selectedDesktopDevice: selectedDesktop?.deviceId,
      selectedMicDevice: audioState.microphoneSources.find((mic) => mic.selected)?.deviceId,
    });

    try {
      if (selectedDesktop) {
        const track = audioStreams[selectedDesktop.deviceId]?.track;
        if (track) {
          audioStreams[selectedDesktop.deviceId]?.volumeControl(audioState.desktopAudioVolume);
          console.debug('🔊 HOST: Set desktop volume:', {
            deviceId: selectedDesktop.deviceId,
            volume: audioState.desktopAudioVolume,
            trackEnabled: track.enabled,
          });
        }
      }

      // set volume for all microphones
      audioState.microphoneSources.forEach((microphone) => {
        if (microphone.selected) {
          const track = audioStreams[microphone.deviceId]?.track;
          if (track) {
            audioStreams[microphone.deviceId]?.volumeControl(audioState.microphoneVolume);
            console.debug('🔊 HOST: Set microphone volume:', {
              deviceId: microphone.deviceId,
              volume: audioState.microphoneVolume,
              trackEnabled: track.enabled,
            });
          }
        }
      });
    } catch (error) {
      console.error('🔊 HOST: Error setting volume:', error);
    }

    volumeChangeTimeout = null;
  }, 50); // 50ms debounce to prevent audio context overload
};

export const initializeMediaManager = () => {
  // Cache previous audio state to detect actual changes
  let previousAudioState: {
    selectedDesktopId?: string;
    selectedMicId?: string;
    desktopMuted?: boolean;
    micMuted?: boolean;
  } = {};

    // Add debug function to window for troubleshooting
    try {
      (window as any).playcastHost = (window as any).playcastHost || {};
      (window as any).playcastHost.debugAudio = () => {
        console.log('🎵 === HOST AUDIO DEBUG ===');
        console.log('Audio State:', state.audioState.currentState);
        console.log('Active Audio Streams:', Object.fromEntries(
          Object.entries(audioStreams).map(([key, stream]) => [
            key,
            stream ? {
              trackId: stream.track.id,
              channel: stream.metadata.channel,
              displayName: stream.metadata.displayName,
              trackEnabled: stream.track.enabled,
              trackReadyState: stream.track.readyState,
              trackMuted: stream.track.muted,
              audioContextState: stream.audioContext.state
            } : 'undefined'
          ])
        ));
        console.log('=== END HOST AUDIO DEBUG ===');
      };

      // Add performance fix function
      (window as any).playcastHost.recoverAudio = () => {
        console.log('🔧 HOST: Emergency audio recovery triggered');
        const audioDevices = Object.keys(audioStreams);
        audioDevices.forEach(deviceId => {
          const stream = audioStreams[deviceId];
          if (stream && stream.audioContext.state === 'suspended') {
            console.log(`🔧 HOST: Resuming suspended audio context for ${deviceId}`);
            stream.audioContext.resume().catch(e => {
              console.error(`Failed to resume audio context for ${deviceId}:`, e);
            });
          }
        });
      };

      console.log('🔧 HOST: Added window.playcastHost.debugAudio() and window.playcastHost.recoverAudio()');
    } catch (e) {
      console.error('Could not set window.playcastHost', e);
    }

  const addTrackListeners = () => {
    const videoListener = (videoState: VideoState) => {
      const allDevices: string[] = [];
      if (videoState.selectedDevice !== '0') {
        if (videoState.selectedDevice === 'display') {
          const activeShadowcams = state.activeShadowcamState.currentState.active;
          activeShadowcams.forEach((shadowcam) => {
            allDevices.push(`display-${shadowcam}`);
          });
        } else if (videoState.selectedDevice === 'followMouse') {
          const activeShadowcams = state.activeShadowcamState.currentState.active;
          activeShadowcams.forEach((shadowcam) => {
            allDevices.push(`followMouse-${shadowcam}`);
          });
        } else {
          allDevices.push(videoState.selectedDevice);
        }
      }
      allDevices.forEach((deviceId) => {
        if (videoStreams[deviceId] === undefined) {
          videoStreams[deviceId] = undefined;
        }
      });
      // remove all devices that are not in the current state
      const currentDevices = Object.keys(videoStreams);
      currentDevices.forEach((deviceId) => {
        if (!allDevices.includes(deviceId)) {
          removeVideoDevice(deviceId);
        }
      });
      avPromises.push(initializeVideoStreams());
      awaitAvPromises();
    };
    state.videoState.addListener((videoState) => {
      videoListener(videoState);
    });
    state.activeShadowcamState.addListener(() => {
      videoListener(state.videoState.currentState);
    });

    const audioListener = (audioState: AudioState) => {
      const selectedDesktop = audioState.desktopAudioSources.find((source) => source.selected);
      const selectedMic = audioState.microphoneSources.find((microphone) => microphone.selected);

      // Check if anything actually changed
      const currentState = {
        selectedDesktopId: selectedDesktop?.deviceId,
        selectedMicId: selectedMic?.deviceId,
        desktopMuted: audioState.desktopAudioMuted,
        micMuted: audioState.microphoneMuted,
      };

      const hasChanges =
        currentState.selectedDesktopId !== previousAudioState.selectedDesktopId ||
        currentState.selectedMicId !== previousAudioState.selectedMicId ||
        currentState.desktopMuted !== previousAudioState.desktopMuted ||
        currentState.micMuted !== previousAudioState.micMuted;

      if (!hasChanges) {
        console.log('🎵 HOST: Audio state unchanged, skipping recalculation');
        return;
      }

      console.log('🎵 HOST: Audio state changed, recalculating audio devices');
      console.log('🔄 HOST: Changes detected:', {
        selectedDesktop: `${previousAudioState.selectedDesktopId} → ${currentState.selectedDesktopId}`,
        selectedMic: `${previousAudioState.selectedMicId} → ${currentState.selectedMicId}`,
        desktopMuted: `${previousAudioState.desktopMuted} → ${currentState.desktopMuted}`,
        micMuted: `${previousAudioState.micMuted} → ${currentState.micMuted}`,
      });

      // Update cached state
      previousAudioState = currentState;

      const allDevices: string[] = [];
      console.log('🎵 HOST: Selected desktop source:', selectedDesktop);

      if (selectedDesktop && selectedDesktop.deviceId !== '0') {
        // Only allow gameCapture if matchmaker override is enabled
        if (selectedDesktop.deviceId === 'gameCapture' && !matchmakerOverrideState.currentState) {
          console.debug('[Media] Game capture selection ignored - matchmaker override is false');
          allDevices.push('desktop'); // Fallback to desktop
        } else {
          allDevices.push(selectedDesktop.deviceId);
          console.log('🎵 HOST: Added desktop audio device:', selectedDesktop.deviceId);
        }
      } else {
        console.log('🎵 HOST: No desktop audio selected (None or invalid)');
      }

      if (selectedMic && selectedMic.deviceId !== '0') {
        allDevices.push(selectedMic.deviceId);
        console.log('🎵 HOST: Added microphone device:', selectedMic.deviceId);
      } else {
        console.log('🎵 HOST: No microphone selected (None or invalid)');
      }

      console.log('🎵 HOST: Target audio devices:', allDevices);
      console.log('🎵 HOST: Current audio streams:', Object.keys(audioStreams));

      // First, remove devices that shouldn't be active
      const currentDevices = Object.keys(audioStreams);
      const devicesToRemove = currentDevices.filter(deviceId => !allDevices.includes(deviceId));

      console.log('🗑️ HOST: Removing audio devices:', devicesToRemove);
      devicesToRemove.forEach((deviceId) => {
        removeAudioDevice(deviceId);
      });

      // Then, mark needed devices for initialization
      allDevices.forEach((deviceId) => {
        if (audioStreams[deviceId] === undefined) {
          console.log('➕ HOST: Marking device for initialization:', deviceId);
          audioStreams[deviceId] = undefined;
        }
      });

      avPromises.push(initializeAudioStreams());
      awaitAvPromises();
    };
    state.audioState.addListener((audioState) => {
      audioListener(audioState);
    });

    // Listen to matchmaker override state changes to update desktop audio sources
    matchmakerOverrideState.addListener(() => {
      populateDesktopAudioSelection();
      audioListener(state.audioState.currentState);
    });

    videoListener(state.videoState.currentState);
    audioListener(state.audioState.currentState);
  };

  const addMuteListeners = () => {
    state.audioState.addListener((audioState) => {
      checkMutedState();
    });
    checkMutedState();
  };

  const addVolumeListeners = () => {
    state.audioState.addListener((audioState) => {
      // Only call setVolume for actual volume/mute changes, not device selection changes
      setVolume();
    });
  };
  addVolumeListeners();
  addTrackListeners();
  addMuteListeners();
};

export const getMediaTrackFromId = (deviceId: string) => {
  const id = deviceId;

  if (audioStreams[id]) {
    return audioStreams[id]?.track;
  }
  if (videoStreams[id]) {
    return videoStreams[id]?.track;
  }
  return null;
};

export const getAllTracks = () => {
  const videoStreamCopy = { ...videoStreams };
  const audioStreamCopy = { ...audioStreams };

  console.log('🎵 HOST: getAllTracks called');
  console.log('🎵 HOST: Raw audioStreams:', Object.fromEntries(
    Object.entries(audioStreams).map(([key, stream]) => [
      key,
      stream ? {
        trackId: stream.track.id,
        channel: stream.metadata.channel,
        displayName: stream.metadata.displayName,
        trackEnabled: stream.track.enabled,
        trackReadyState: stream.track.readyState
      } : 'undefined'
    ])
  ));

  // dont send desktop audio if selected is deviceId 0
  const selectedDesktopSource = state.audioState.currentState.desktopAudioSources.find((source) => source.selected);
  const selectedMicrophoneSource = state.audioState.currentState.microphoneSources.find((source) => source.selected);

  console.log('🎵 HOST: Selected sources:', {
    desktop: selectedDesktopSource,
    microphone: selectedMicrophoneSource
  });

  if (selectedDesktopSource?.deviceId === '0') {
    // remove from audioStreamCopy
    delete audioStreamCopy.desktop;
    console.log('🎵 HOST: Removed desktop audio (None selected)');
  }
  if (selectedMicrophoneSource?.deviceId === '0') {
    delete audioStreamCopy['0'];
    console.log('🎵 HOST: Removed microphone (None selected)');
  }

  const allOutput = {
    ...videoStreamCopy,
    ...audioStreamCopy,
  };

  console.log('🎵 HOST: Final tracks being sent:', Object.fromEntries(
    Object.entries(allOutput).map(([key, stream]) => [
      key,
      stream ? {
        trackId: stream.track.id,
        channel: stream.metadata.channel,
        displayName: stream.metadata.displayName,
        trackEnabled: stream.track.enabled,
        trackReadyState: stream.track.readyState
      } : 'undefined'
    ])
  ));

  return allOutput;
};

export const getTracksForProfile = (profile: PlaycastSendProfile | undefined) => {
  type TrackMap = Partial<{
    [channel in PlaycastChannels]: { track: MediaStreamTrack; metadata: PlaycastPeerMediaMetadata };
  }>;
  if (!profile) {
    return {};
  }
  const tracks: TrackMap = {};
  const allTracks = getAllTracks();
  // console.log('[Media] All tracks', allTracks);
  profile.forEach((channel) => {
    Object.values(allTracks).forEach((track) => {
      if (track) {
        if (track.metadata.channel === channel) {
          tracks[channel] = {
            track: track.track,
            metadata: track.metadata,
          };
        }
      }
    });
  });
  console.log('[Media] Tracks for profile', tracks);
  return tracks;
};

export const getMediaTrackFromIdAsPromise = (deviceId: string) => {
  const id = deviceId;
  const isShadowcamPreview = id.startsWith('display-') || id.startsWith('followMouse-');

  const getTrack = () => {
    if (audioStreams[id]) {
      return audioStreams[id]?.track;
    }
    if (videoStreams[id]) {
      return videoStreams[id]?.track;
    }
    return null;
  };

  return new Promise<MediaStreamTrack | null>((resolve) => {
    const track = getTrack();
    if (!track) {
      if (isShadowcamPreview) {
        void refreshDisplayShadowcamStreams();
      }
      const listener = setInterval(() => {
        const newTrack = getTrack();
        if (newTrack) {
          clearInterval(listener);
          resolve(newTrack);
        }
      }, 200);
      const timeoutMs = isShadowcamPreview ? 30_000 : 5_000;
      setTimeout(() => {
        clearInterval(listener);
        resolve(null);
      }, timeoutMs);
    } else {
      resolve(track);
    }
  });
};
